import axios from '../api/axios';

const BASE = '/api/ee/mail';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MailServerConfig {
  id: number;
  mail_api_url?: string;
  mail_api_key_set: boolean;
  webmail_url?: string;
  is_active: boolean;
  updated_at?: string;
}

export interface MailServerConfigUpdate {
  mail_api_url?: string;
  mail_api_key?: string;
  webmail_url?: string;
  is_active?: boolean;
}

export interface MailDomainTenantRef {
  org_id: number;
  org_name: string;   // organizations.org_name 為 String（非多語系）
}

export interface MailDomain {
  domain_code: string;
  server_config_id?: number;
  mail_api_url?: string;
  default_quota?: string;
  tenant_enabled: boolean;
  admin_only: boolean;
  is_configured: boolean;
  logo_file_id?: number | null;        // #21 webmail branding
  favicon_file_id?: number | null;     // #21 webmail branding
  office_registered: boolean;          // 3.2f Office 支援（Collabora alias register）
  office_registered_at?: string | null;
  used_by_tenants?: MailDomainTenantRef[];  // #21 admin verify
}

export interface MailDomainUpdate {
  server_config_id?: number | null;
  default_quota?: string | null;
  tenant_enabled?: boolean;
  admin_only?: boolean;
}

export interface UserMailbox {
  id: number;
  email_address: string;
  username: string;
  domain: string;
  quota?: string;
  quota_used?: string;
  quota_pct?: number;
  quota_synced_at?: string;
  is_active: boolean;
  created_at?: string;
  server_exists: boolean;
}

export interface AdminMailbox extends UserMailbox {
  user_id?: number;
  org_id?: number;
  in_db: boolean;
}

export interface MailboxApplyRequest {
  domain: string;
  username: string;
  current_password: string;
}

export interface MailboxAvailability {
  available: boolean;
  reason?: string;
}

// ─── Server Config APIs ───────────────────────────────────────────────────────

export const getServerConfig = () =>
  axios.get<MailServerConfig>(`${BASE}/server-config`).then(r => r.data);

export const updateServerConfig = (data: MailServerConfigUpdate) =>
  axios.put<MailServerConfig>(`${BASE}/server-config`, data).then(r => r.data);

export const testServerConnection = () =>
  axios.post<{ connected: boolean; mail_api_url: string }>(`${BASE}/server-config/test`).then(r => r.data);

// ─── Domain APIs ──────────────────────────────────────────────────────────────

export const listDomains = () =>
  axios.get<MailDomain[]>(`${BASE}/domains`).then(r => r.data);

export const getDomain = (domainCode: string) =>
  axios.get<MailDomain>(`${BASE}/domains/${domainCode}`).then(r => r.data);

export const createDomain = (domainCode: string, data: MailDomainUpdate) =>
  axios.post<MailDomain>(`${BASE}/domains/${domainCode}`, data).then(r => r.data);

export const updateDomain = (domainCode: string, data: MailDomainUpdate) =>
  axios.put<MailDomain>(`${BASE}/domains/${domainCode}`, data).then(r => r.data);

export const deleteDomain = (domainCode: string) =>
  axios.delete(`${BASE}/domains/${domainCode}`);

// 3.2f · toggle Office 支援（Collabora alias register）· 主網域 register 一次 subdomain zero-touch
export const toggleOfficeRegistration = (domainCode: string, enabled: boolean) =>
  axios.patch<MailDomain>(`${BASE}/domains/${domainCode}/office-toggle`, { enabled }).then(r => r.data);

// #21 webmail branding：admin 上傳 mail_domain logo / favicon
const uploadDomainAsset = (domainCode: string, kind: 'logo' | 'favicon', file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return axios.post<MailDomain>(`${BASE}/domains/${domainCode}/${kind}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};
export const uploadDomainLogo = (domainCode: string, file: File) =>
  uploadDomainAsset(domainCode, 'logo', file);
export const uploadDomainFavicon = (domainCode: string, file: File) =>
  uploadDomainAsset(domainCode, 'favicon', file);

// 移除網域 logo/favicon（reference 清空；回更新後的 domain）
export const deleteDomainLogo = (domainCode: string) =>
  axios.delete<MailDomain>(`${BASE}/domains/${domainCode}/logo`).then(r => r.data);
export const deleteDomainFavicon = (domainCode: string) =>
  axios.delete<MailDomain>(`${BASE}/domains/${domainCode}/favicon`).then(r => r.data);

// #21 public webmail branding URL（供預覽 / Roundcube）
// cache-bust：帶 file_id 當 ?v= → replace 後瀏覽器即時取新圖（porsche 2026-08-25）
export const mailDomainLogoUrl = (domainCode: string, fileId?: number | string | null) =>
  `${BASE}/domains/${domainCode}/logo.png${fileId ? `?v=${fileId}` : ''}`;
export const mailDomainFaviconUrl = (domainCode: string, fileId?: number | string | null) =>
  `${BASE}/domains/${domainCode}/favicon.png${fileId ? `?v=${fileId}` : ''}`;

export const listAvailableDomains = () =>
  axios.get<MailDomain[]>(`${BASE}/my-mailbox/available-domains`).then(r => r.data);

// ─── My Mailbox APIs ──────────────────────────────────────────────────────────

export const getMyMailbox = () =>
  axios.get<UserMailbox | null>(`${BASE}/my-mailbox`).then(r => r.data);

export const checkAvailability = (domain: string, username: string) =>
  axios.get<MailboxAvailability>(`${BASE}/my-mailbox/check/${domain}/${username}`).then(r => r.data);

export const applyMailbox = (data: MailboxApplyRequest) =>
  axios.post<UserMailbox>(`${BASE}/my-mailbox`, data).then(r => r.data);

export const rebuildMyMailbox = () =>
  axios.post<UserMailbox>(`${BASE}/my-mailbox/rebuild`).then(r => r.data);

export const deleteMyMailbox = () =>
  axios.delete(`${BASE}/my-mailbox`);

// ─── Admin Mailbox APIs ───────────────────────────────────────────────────────

export const listAllMailboxes = (params?: { org_id?: number; domain?: string }) =>
  axios.get<AdminMailbox[]>(`${BASE}/accounts`, { params }).then(r => r.data);

export const adminCreateMailbox = (email: string, password: string) =>
  axios.post<AdminMailbox>(`${BASE}/accounts`, { email, password }).then(r => r.data);

export const setMailboxQuota = (mailboxId: number, quota: string | null) =>
  axios.put<AdminMailbox>(`${BASE}/accounts/${mailboxId}/quota`, { quota }).then(r => r.data);

export const updateMailboxPassword = (mailboxId: number, password: string) =>
  axios.put(`${BASE}/accounts/${mailboxId}/password`, { password });

export const updateMailboxPasswordByEmail = (emailAddress: string, password: string) =>
  axios.put(`${BASE}/accounts/by-email/${encodeURIComponent(emailAddress)}/password`, { password });

export const restrictMailbox = (mailboxId: number) =>
  axios.post(`${BASE}/accounts/${mailboxId}/restrict`);

export const unrestrictMailbox = (mailboxId: number) =>
  axios.delete(`${BASE}/accounts/${mailboxId}/restrict`);

export const syncMailboxUsage = (mailboxId: number) =>
  axios.post<AdminMailbox>(`${BASE}/accounts/${mailboxId}/sync-usage`).then(r => r.data);

export const deleteMailboxAdmin = (mailboxId: number) =>
  axios.delete(`${BASE}/accounts/${mailboxId}`);

export const deleteMailboxByEmail = (emailAddress: string) =>
  axios.delete(`${BASE}/accounts/by-email/${encodeURIComponent(emailAddress)}`);

// ─── Webmail SSO ──────────────────────────────────────────────────────────────

export const getWebmailSsoUrl = (composeTo?: string) =>
  axios.get<{ url: string }>(`${BASE}/webmail-token${composeTo ? `?compose_to=${encodeURIComponent(composeTo)}` : ''}`).then(r => r.data.url);
